import React from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import { useT } from '../i18n/useT';

interface RouteHeroProps {
  eyebrow: string;
  title: string;
  subtitle: string;
}

const RouteHero: React.FC<RouteHeroProps> = ({ eyebrow, title, subtitle }) => {
  const { r } = useT();

  return (
    <Box
      component="section"
      sx={{
        pt: { xs: '110px', md: '130px' },
        pb: { xs: 6, md: 8 },
        backgroundColor: 'rgb(13, 13, 37)',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at 15% 25%, rgba(0, 245, 255, 0.10) 0%, transparent 45%), radial-gradient(circle at 85% 75%, rgba(255, 0, 255, 0.08) 0%, transparent 45%)',
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Button
            component={RouterLink}
            to="/"
            startIcon={<ArrowBackIosNewRoundedIcon sx={{ fontSize: '0.85rem' }} />}
            sx={{
              mb: 3,
              color: 'rgba(255, 255, 255, 0.75)',
              fontSize: '0.85rem',
              fontWeight: 600,
              textTransform: 'none',
              px: 1.5,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.06)',
                color: 'white',
              },
            }}
          >
            {r.routes.backToOverview}
          </Button>
          <Typography
            component="span"
            sx={{
              display: 'block',
              color: 'rgba(255, 255, 255, 0.55)',
              fontWeight: 700,
              fontSize: '0.78rem',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              mb: 2,
            }}
          >
            {eyebrow}
          </Typography>
          <Typography
            variant="h1"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '2rem', sm: '2.6rem', md: '3.4rem' },
              lineHeight: 1.1,
              mb: 2,
              letterSpacing: '-0.02em',
              maxWidth: '880px',
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: { xs: '1rem', md: '1.15rem' },
              lineHeight: 1.55,
              maxWidth: '760px',
            }}
          >
            {subtitle}
          </Typography>
        </motion.div>
      </Container>
    </Box>
  );
};

export default RouteHero;
